import logo from "../assets/images/logo.svg";
import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { IoCall, IoClose, IoMenu } from "react-icons/io5";
import { PRIMARY_PHONE } from "../lib/contact";
import { motion, AnimatePresence } from "framer-motion";

const navItems = [
  { path: "/services", label: "Services" },
  { path: "/projects-gallery", label: "Projects" },
  { path: "/credentials", label: "Credentials" },
  { path: "/clients", label: "Clients" },
  { path: "/careers", label: "Careers" },
  { path: "/about-us", label: "About Us" },
];

const Navbar = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Close the mobile menu whenever the route changes
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  return (
    <header
      className={`fixed top-0 inset-x-0 z-40 transition-all duration-300 border-b-4 border-brand-yellow ${
        scrolled
          ? "bg-white/95 backdrop-blur shadow-lg"
          : "bg-white shadow-sm"
      }`}
    >
      <div className="max-w-7xl mx-auto h-16 md:h-20 px-4 sm:px-6 lg:px-8 flex items-center justify-between">
        <Link to="/" aria-label="R.C. Bidave home" className="shrink-0">
          <img
            src={logo}
            alt="R.C. Bidave logo"
            className="h-11 md:h-14 w-auto transition-transform duration-300 hover:scale-105"
          />
        </Link>

        {/* Desktop navigation */}
        <nav className="hidden md:flex items-center gap-1 lg:gap-2" aria-label="Main">
          {navItems.map((item) => {
            const active = location.pathname === item.path;
            return (
              <Link
                key={item.path}
                to={item.path}
                aria-current={active ? "page" : undefined}
                className={`relative px-3 lg:px-4 py-2 text-sm lg:text-[15px] font-semibold rounded-md transition-colors duration-200 ${
                  active
                    ? "text-brand-navy"
                    : "text-gray-600 hover:text-brand-navy"
                }`}
              >
                {item.label}
                <span
                  className={`absolute left-3 right-3 lg:left-4 lg:right-4 -bottom-0.5 h-0.5 rounded-full bg-brand-yellow origin-left transition-transform duration-300 ${
                    active ? "scale-x-100" : "scale-x-0"
                  }`}
                />
              </Link>
            );
          })}
          <Link
            to="/contact-us"
            className={`ml-2 lg:ml-4 px-5 py-2.5 rounded-full text-sm font-bold shadow-md transition-all duration-300 hover:shadow-lg hover:-translate-y-0.5 ${
              location.pathname === "/contact-us"
                ? "bg-brand-navy text-white"
                : "bg-brand-yellow text-brand-navy hover:bg-brand-gold"
            }`}
          >
            Contact Us
          </Link>
        </nav>

        {/* Mobile: one-tap call + menu toggle */}
        <div className="flex items-center gap-1 md:hidden">
        <a
          href={`tel:${PRIMARY_PHONE.tel}`}
          aria-label={`Call ${PRIMARY_PHONE.display}`}
          className="inline-flex items-center gap-1.5 rounded-full bg-brand-yellow px-3.5 py-2 text-sm font-bold text-brand-navy shadow-sm"
        >
          <IoCall aria-hidden="true" /> Call
        </a>
        <button
          type="button"
          onClick={() => setIsMobileMenuOpen((open) => !open)}
          aria-label={isMobileMenuOpen ? "Close menu" : "Open menu"}
          aria-expanded={isMobileMenuOpen}
          className="text-brand-navy text-3xl p-2 -mr-2 rounded-md"
        >
          {isMobileMenuOpen ? <IoClose /> : <IoMenu />}
        </button>
        </div>
      </div>

      {/* Mobile navigation */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.nav
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25, ease: "easeInOut" }}
            className="md:hidden overflow-hidden bg-white border-t border-gray-100"
            aria-label="Mobile"
          >
            <ul className="flex flex-col p-3">
              {[...navItems, { path: "/contact-us", label: "Contact Us" }].map((item) => {
                const active = location.pathname === item.path;
                return (
                  <li key={item.path}>
                    <Link
                      to={item.path}
                      aria-current={active ? "page" : undefined}
                      className={`block px-4 py-3 rounded-lg font-semibold transition-colors ${
                        active
                          ? "bg-brand-navy text-white"
                          : "text-gray-700 hover:bg-brand-yellow/30 hover:text-brand-navy"
                      }`}
                    >
                      {item.label}
                    </Link>
                  </li>
                );
              })}
            </ul>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Navbar;
